import React, { useState, useEffect, useCallback } from "react";
import { BookMarked, Plus, Check, Pencil, Trash2, Loader, Eye, EyeOff } from "lucide-react";
import PageHeader from "./PageHeader";

function BausteinCard({ b, onPatch, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [titel, setTitel] = useState(b.titel);
  const [leitsatz, setLeitsatz] = useState(b.leitsatz || "");
  const [inhalt, setInhalt] = useState(b.inhalt || "");
  const [busy, setBusy] = useState(false);
  const isWert = b.typ === "wert";

  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  const save = () =>
    run(async () => {
      const body = { titel: titel.trim(), inhalt };
      if (isWert) body.leitsatz = leitsatz;
      await onPatch(b.id, body);
      setEditing(false);
    });

  return (
    <div className={`baustein-card card${b.aktiv ? "" : " baustein-inactive"}`}>
      {editing ? (
        <div className="baustein-edit">
          <input className="ai-prompt-input" value={titel} onChange={(e) => setTitel(e.target.value)} placeholder="Titel" />
          {isWert && (
            <input className="ai-prompt-input" value={leitsatz} onChange={(e) => setLeitsatz(e.target.value)} placeholder="Leitsatz (z.B. „Ich diene zuerst anderen …“)" />
          )}
          <textarea className="ai-prompt-input feedback-textarea" rows={6} value={inhalt} onChange={(e) => setInhalt(e.target.value)} placeholder="Inhalt" />
          <div className="rule-actions">
            <button className="btn-ai-go" disabled={!titel.trim() || busy} onClick={save}>
              {busy ? <Loader size={13} className="spin" /> : <Check size={13} />} Speichern
            </button>
            <button className="btn-ai-cancel" onClick={() => { setEditing(false); setTitel(b.titel); setLeitsatz(b.leitsatz || ""); setInhalt(b.inhalt || ""); }}>Abbrechen</button>
          </div>
        </div>
      ) : (
        <>
          <div className="baustein-head">
            <h4 className="baustein-titel">{b.titel}</h4>
            {!b.aktiv && <span className="baustein-badge-off">inaktiv</span>}
          </div>
          {b.leitsatz && <p className="baustein-leitsatz">„{b.leitsatz}“</p>}
          {b.inhalt && <p className="baustein-inhalt">{b.inhalt}</p>}
          <div className="rule-actions">
            <button className="btn-ai-auto" onClick={() => setEditing(true)}><Pencil size={13} /> Bearbeiten</button>
            <button className="btn-ai-auto" disabled={busy} onClick={() => run(() => onPatch(b.id, { aktiv: !b.aktiv }))}>
              {b.aktiv ? <><EyeOff size={13} /> Deaktivieren</> : <><Eye size={13} /> Aktivieren</>}
            </button>
            <button className="btn-rule-del" disabled={busy} onClick={() => { if (window.confirm("Baustein in den Papierkorb verschieben? Du kannst ihn dort 30 Tage lang wiederherstellen.")) run(() => onDelete(b.id)); }}>
              <Trash2 size={13} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function UneqBausteinePage() {
  const [bausteine, setBausteine] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    fetch("/api/uneq-bausteine", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { setBausteine(Array.isArray(d.bausteine) ? d.bausteine : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPatch = async (id, body) => {
    const res = await fetch(`/api/uneq-bausteine/${id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify(body),
    });
    const d = await res.json();
    if (!res.ok) { alert("Fehler: " + (d.error || res.status)); return; }
    setBausteine((prev) => prev.map((x) => (x.id === id ? d.baustein : x)));
  };

  const onDelete = async (id) => {
    const res = await fetch(`/api/uneq-bausteine/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) { alert("Löschen fehlgeschlagen"); return; }
    setBausteine((prev) => prev.filter((x) => x.id !== id));
  };

  const onAdd = async (typ) => {
    const titel = window.prompt(typ === "wert" ? "Name des Werts:" : "Titel des Bausteins:");
    if (!titel || !titel.trim()) return;
    const res = await fetch("/api/uneq-bausteine", {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ typ, titel: titel.trim() }),
    });
    const d = await res.json();
    if (!res.ok) { alert("Fehler: " + (d.error || res.status)); return; }
    setBausteine((prev) => [...prev, d.baustein]);
  };

  const werte = bausteine.filter((b) => b.typ === "wert");
  const textbausteine = bausteine.filter((b) => b.typ === "baustein");

  return (
    <>
      <PageHeader
        eyebrow="UNEQ"
        title="UNEQ-Werte & Bausteine"
        subtitle="Die UNEQ-Werte fließen in jede UNEQ-Stellenanzeige ein; die Textbausteine sind feste Abschnitte, die übernommen werden. Hier pflegst du beides zentral – Änderungen gelten für alle künftigen Anzeigen."
      />

      {loading ? (
        <div className="rule-empty"><Loader size={18} className="spin" /> Laden …</div>
      ) : (
        <div className="rule-sections">
          <section className="rule-section">
            <div className="baustein-section-head">
              <h3 className="rule-section-title">Werte <span className="rule-count">{werte.length}</span></h3>
              <button className="btn-secondary-sm" onClick={() => onAdd("wert")}><Plus size={14} /> Wert</button>
            </div>
            {werte.length === 0 ? <p className="rule-section-empty">Noch keine Werte.</p>
              : werte.map((b) => <BausteinCard key={b.id} b={b} onPatch={onPatch} onDelete={onDelete} />)}
          </section>

          <section className="rule-section">
            <div className="baustein-section-head">
              <h3 className="rule-section-title">Textbausteine <span className="rule-count">{textbausteine.length}</span></h3>
              <button className="btn-secondary-sm" onClick={() => onAdd("baustein")}><Plus size={14} /> Baustein</button>
            </div>
            {textbausteine.length === 0 ? <p className="rule-section-empty">Noch keine Bausteine.</p>
              : textbausteine.map((b) => <BausteinCard key={b.id} b={b} onPatch={onPatch} onDelete={onDelete} />)}
          </section>
        </div>
      )}
    </>
  );
}
